import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import type {UpdateLabelMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelMutation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsCacheData} from './types'
import {formatFormErrorMessage} from './utils/format-form-error-message'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseEditLabelOptions = {
  labelId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setEditSubmissionErrors: (v: string) => void
  setIsEditDialogOpen: (v: boolean) => void
}

export function useEditLabel({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setEditSubmissionErrors,
  setIsEditDialogOpen,
}: UseEditLabelOptions) {
  const queryClient = useQueryClient()

  return useCallback(
    async (
      input: {
        name: string
        description: string
        color: string
      },
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      setEditSubmissionErrors('')

      const previousData = queryKey ? queryClient.getQueryData<LabelsCacheData>(queryKey) : undefined
      const failWith = (message: string) => {
        if (queryKey && previousData) {
          queryClient.setQueryData(queryKey, previousData)
        }
        setEditSubmissionErrors(message)
        setIsSubmitting(false)
        setIsEditDialogOpen(true)
      }

      // Optimistically update the label in the TanStack cache
      if (queryKey) {
        queryClient.setQueryData<LabelsCacheData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === labelId)
            if (edge?.node) {
              edge.node.name = input.name
              edge.node.nameHTML = input.name
              edge.node.description = input.description
              edge.node.color = input.color
            }
          }),
        )
      }

      // Optimistically close the dialog
      setIsEditDialogOpen(false)

      try {
        const response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}`, {
          method: 'PATCH',
          body: {
            name: input.name,
            description: input.description,
            color: input.color,
          },
        })

        if (response.ok) {
          const result: {data: UpdateLabelMutationMutation; errors?: Array<{message: string}>} = await response.json()
          const updatedLabel = result.data.updateLabel?.label
          const errors = result.errors

          if (errors && errors.length > 0) {
            failWith(formatFormErrorMessage(errors))
            return
          }

          if (!updatedLabel) {
            failWith(LABELS.editLabelError)
            return
          }

          // Write the server response into the cache to ensure consistency
          if (queryKey && updatedLabel) {
            queryClient.setQueryData<LabelsCacheData>(
              queryKey,
              updateLabelsCache(labels => {
                const edge = labels.edges.find(e => e?.node?.id === labelId)
                if (edge?.node) {
                  edge.node.name = updatedLabel.name
                  edge.node.nameHTML = updatedLabel.nameHTML
                  edge.node.description = updatedLabel.description
                  edge.node.color = updatedLabel.color
                }
              }),
            )
          }
        } else {
          failWith(LABELS.editLabelError)
        }
      } catch {
        failWith(LABELS.editLabelError)
      }
    },
    [labelId, repositoryNameWithOwner, queryKey, queryClient, setEditSubmissionErrors, setIsEditDialogOpen],
  )
}
